// src/component/DailyPostsAdminPage.js
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../api/firebase";
import { useNavigate } from "react-router-dom";

import Post from "./Post";
import { AppThemeContext } from "./AppShell";
import AddPostDialog from "./AddPostDialog";

const PAYMENT_TERMS = [1, 2, 3, 4];
const ALLOWED_SEKAIMURA_KEY = "sekaiMura";

const normalizeTerms = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((t) => String(t).trim())
    .filter(Boolean);

const truncate = (text, max = 180) =>
  text && text.length > max ? text.slice(0, max) + "…" : text || "";

// 画像用：Post と同じロジックで「第一枚目」を拾う
const getFirstImageUrl = (media) => {
  if (!media || media.type !== "image" || !media.src) return null;

  let flattened = [];

  if (Array.isArray(media.src)) {
    flattened = media.src.flatMap((item) => {
      if (!item) return [];
      if (Array.isArray(item.url)) return item.url;
      if (typeof item === "string") return [item];
      if (typeof item.url === "string") return [item.url];
      return [];
    });
  } else if (typeof media.src === "string") {
    flattened = [media.src];
  } else if (media.src && typeof media.src.url === "string") {
    flattened = [media.src.url];
  }

  return flattened[0] || null;
};

export default function DailyPostsAdminPage() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext);

  const [posts, setPosts] = useState([]);
  const [previewPost, setPreviewPost] = useState(null);

  // Firestore 購読
  useEffect(() => {
    const q = query(collection(db, "posts"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPosts(list);
    });
    return () => unsub();
  }, []);

  const handleOpenPreview = (post) => setPreviewPost(post);
  const handleClosePreview = () => setPreviewPost(null);

  // 表示中／非表示 トグル
  const handleToggleVisible = async (post) => {
    const currentlyVisible = post.isVisible !== false;
    const next = !currentlyVisible;
    try {
      await updateDoc(doc(db, "posts", post.id), { isVisible: next });
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, isVisible: next } : p))
      );
    } catch (e) {
      console.error("Failed to update isVisible:", e);
    }
  };

  // allowedTerm トグル（期 & セカイムラ共通）
  const handleToggleTerm = async (post, termValue) => {
    const termStr = String(termValue);
    const current = normalizeTerms(post.allowedTerm);
    const has = current.includes(termStr);
    const next = has
      ? current.filter((t) => t !== termStr)
      : [...current, termStr];

    try {
      await updateDoc(doc(db, "posts", post.id), { allowedTerm: next });
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, allowedTerm: next } : p))
      );
    } catch (e) {
      console.error("Failed to update allowedTerm:", e);
    }
  };

  const surface = theme?.surfaceBg || "#FFFFFF";
  const border = "rgba(0,0,0,0.06)";
  const textBase = theme?.railText || "rgba(30,30,60,0.9)";
  const accent = theme?.railActive || "#5A35FF";

  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        pb: { xs: 3, sm: 4 },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN / DAILY POSTS
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.03em",
              color: textBase,
            }}
          >
    最新情報管理
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}
          >
            「最新情報」に出すポストの公開範囲と内容を確認できます。
            行をクリックすると、実際の表示イメージでプレビューします。
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/admin")}
          >
            管理者メニューに戻る
          </Button>
            <AddPostDialog />
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* 一覧（1 列） */}
      <Grid container spacing={2.5}>
        {posts.map((post) => {
          const contentText = truncate(post.content || "");
          const ts = post.timestamp?.toDate
            ? post.timestamp.toDate()
            : post.timestamp || null;
          const dateLabel = ts
            ? ts.toLocaleDateString("ja-JP")
            : "日付未設定";

          const allowedSet = new Set(normalizeTerms(post.allowedTerm));
          const firstImageUrl = getFirstImageUrl(post.media);
          const hasVideo = post.media?.type === "video";
          const isVisible = post.isVisible !== false;

          return (
            <Grid item xs={12} key={post.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${isVisible ? border : "rgba(220,38,38,0.35)"}`,
                  bgcolor: surface,
                  boxShadow: "0 18px 40px rgba(15,10,40,0.08)",
                  opacity: isVisible ? 1 : 0.7,
                  position: "relative",
                }}
              >
                <CardActionArea
                  onClick={() => handleOpenPreview(post)}
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "stretch",
                    gap: 2,
                    "&:hover": {
                      boxShadow: "0 22px 50px rgba(15,10,40,0.12)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all .18s ease-out",
                  }}
                >
                  {/* 左のサムネイル */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 180,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.9)",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                        boxShadow: "0 10px 26px rgba(0,0,0,0.18)",
                        "&::before": {
                          content: '""',
                          display: "block",
                          paddingTop: "56.25%", // 16:9
                        },
                      }}
                    >
                      {/* 中身 */}
                      {firstImageUrl ? (
                        <img
                          src={firstImageUrl}
                          alt="thumbnail"
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                              `linear-gradient(135deg, ${accent}33, ${accent}11)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {hasVideo && (
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                bgcolor: "rgba(255,255,255,0.96)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow:
                                  "0 10px 24px rgba(0,0,0,0.28)",
                              }}
                            >
                              <PlayArrowRoundedIcon
                                sx={{
                                  fontSize: 30,
                                  color: accent,
                                  ml: 0.3,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* 日付バッジ */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1.4,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: "rgba(255,255,255,0.96)",
                            fontSize: 11,
                            display: "inline-block",
                          }}
                        >
                          {dateLabel}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* 右側テキスト＆チェック */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {/* 表示中／非表示 ステータス＋トグル */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Chip
                        size="small"
                        icon={
                          isVisible ? (
                            <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                          )
                        }
                        label={isVisible ? "表示中" : "非表示（ホームに出ません）"}
                        color={isVisible ? "success" : "default"}
                        variant={isVisible ? "filled" : "outlined"}
                        sx={{
                          fontSize: 11,
                          height: 22,
                          color: isVisible ? "#fff" : "rgba(220,38,38,0.9)",
                          borderColor: isVisible
                            ? undefined
                            : "rgba(220,38,38,0.5)",
                          "& .MuiChip-icon": {
                            color: isVisible ? "#fff" : "rgba(220,38,38,0.9)",
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={isVisible}
                            onChange={() => handleToggleVisible(post)}
                          />
                        }
                        label="表示"
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": { fontSize: 12 },
                        }}
                      />
                    </Box>

                    {/* 本文（1〜2行） */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 14,
                        color: textBase,
                        lineHeight: 1.7,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {contentText || "（本文未設定）"}
                    </Typography>

                    {/* allowedTerm チェック群 */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 0.5,
                      }}
                      onClick={(e) => e.stopPropagation()} // 行クリックと分離
                    >
                      {PAYMENT_TERMS.map((term) => (
                        <FormControlLabel
                          key={term}
                          control={
                            <Checkbox
                              size="small"
                              checked={allowedSet.has(String(term))}
                              onChange={() =>
                                handleToggleTerm(post, term)
                              }
                            />
                          }
                          label={`${term}期`}
                          sx={{
                            m: 0,
                            mr: 1.5,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 12,
                            },
                          }}
                        />
                      ))}

                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allowedSet.has(ALLOWED_SEKAIMURA_KEY)}
                            onChange={() =>
                              handleToggleTerm(post, ALLOWED_SEKAIMURA_KEY)
                            }
                          />
                        }
                        label="セカイムラ民"
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: 12,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* プレビューダイアログ：Post をそのまま表示 */}
      <Dialog
        open={Boolean(previewPost)}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            pb: 1,
          }}
        >
    基本情報プレビュー
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {previewPost && <Post post={previewPost} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
